import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/AppIcon';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import { updateRefereeProfile } from '@/api/TournamentManagement/refereeApi';
import { RefereeCertification } from '@/models/tournament';
import type { RefereeView } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';

interface EditRefereeModalProps {
  isOpen: boolean;
  referee: RefereeView;
  onClose: () => void;
  onSuccess: () => void;
}

const EditRefereeModal: React.FC<EditRefereeModalProps> = ({
  isOpen,
  referee,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('tournament');
  const { code: currencyCode } = useCurrency();

  const [certification, setCertification] = useState<string>(String(referee.certification));
  const [pointToCurrencyRate, setPointToCurrencyRate] = useState<string>(
    referee.pointToCurrencyRate != null && referee.pointToCurrencyRate > 0 ? String(referee.pointToCurrencyRate) : '',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (certification === '') newErrors.certification = t('Certification is required');
    if (pointToCurrencyRate.trim() !== '' && (Number.isNaN(Number(pointToCurrencyRate)) || Number(pointToCurrencyRate) < 0))
      newErrors.pointToCurrencyRate = t('Rate must be a non-negative number');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const result = await updateRefereeProfile({
        id: referee.id,
        certification: Number(certification) as RefereeCertification,
        // Empty clears the override (server stores null and the company-wide rule rate applies).
        pointToCurrencyRate: pointToCurrencyRate.trim() === '' ? null : Number(pointToCurrencyRate),
      });

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to update referee profile'));
        return;
      }

      onSuccess();
      handleClose();
    } catch {
      setApiError(t('Failed to update referee profile'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setApiError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="lg" data-testid="edit-referee-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="Pencil" size={22} className="me-2 text-amber-600" />
            {t('Edit Referee')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-5">
            {/* Referee Name (read-only) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Name:', { nsSeparator: false })}
              </label>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-md">
                <Icon name="User" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{referee.name}</span>
              </div>
            </div>

            {/* Certification */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Certification')} *
              </label>
              <Select
                value={certification}
                onChange={(value: string) => {
                  setCertification(value);
                  if (errors.certification) setErrors((prev) => ({ ...prev, certification: '' }));
                }}
                options={[
                  { label: t('National Level A'), value: String(RefereeCertification.NationalLevelA) },
                  { label: t('AFC Elite'), value: String(RefereeCertification.AFCElite) },
                  { label: t('FIFA International'), value: String(RefereeCertification.FIFAInternational) },
                ]}
                placeholder={t('Select certification')}
                className={errors.certification ? 'border-red-500' : ''}
                data-testid="edit-referee-certification"
              />
              {errors.certification && (
                <p className="text-xs text-destructive mt-1">{errors.certification}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{t('National Level A: Local tournaments | AFC Elite: Regional | FIFA International: Global', { nsSeparator: false })}</p>
            </div>

            {/* Point to Currency Rate */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Point to Currency Rate')} {currencyCode && `(${currencyCode})`} <span className="text-muted-foreground font-normal">({t('Optional')})</span>
              </label>
              <Input
                type="number"
                value={pointToCurrencyRate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPointToCurrencyRate(e.target.value);
                  if (errors.pointToCurrencyRate) setErrors((prev) => ({ ...prev, pointToCurrencyRate: '' }));
                }}
                placeholder={t('Leave empty to use company default')}
                min="0"
                step="0.01"
                aria-invalid={!!errors.pointToCurrencyRate}
                data-testid="edit-referee-currency-rate"
              />
              {errors.pointToCurrencyRate && (
                <p role="alert" className="text-xs text-destructive mt-1">{errors.pointToCurrencyRate}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{t('Overrides the company-wide rate from Work Point Settings for this referee only. Leave empty to use the company-wide rate.')}</p>
            </div>

            {/* API Error */}
            {apiError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center" data-testid="edit-referee-api-error">
                <Icon name="AlertCircle" size={16} className="text-red-600 dark:text-red-400 me-2 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{apiError}</p>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              data-testid="edit-referee-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              iconName="Save"
              data-testid="edit-referee-submit-btn"
            >
              {isSubmitting ? t('Saving...') : t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRefereeModal;
